import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
import {
  BarChart3,
  ChevronDown,
  GraduationCap,
  Inbox,
  LayoutGrid,
  LayoutTemplate,
  MessageCircle,
  Play,
  Plus,
  Search,
  Triangle,
} from "lucide-react";
/**
 * Agents (KI-Agenten) hero module — 1:1 port of the prototype's `isAgents`
 * sidebar (Hausverwaltung Hero.dc.html ~L520) + content (~L1322) blocks and the
 * `agents` data array (~L2693). Static product mock: a hand-rolled sidebar
 * (Agents nav group + Vorlagen/Training/Statistiken) plus a 3-column grid of 6
 * agent cards, each with a colored triangle badge and an inert Aus/Vorschlag/
 * Agent mode toggle.
 */
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import {
  CARD_BORDER,
  H,
  MODULE_HEIGHT,
  SIDEBAR_WIDTH,
} from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

type AgentMode = "aus" | "vorschlag" | "agent";

interface AgentCard {
  readonly letter: string;
  readonly color: string;
  readonly title: string;
  readonly desc: string;
  readonly mode: AgentMode;
}

/** The 6 agent cards, in prototype order (triangle badge colors verbatim). */
function agents(t: Translate): readonly AgentCard[] {
  return [
    {
      letter: "S",
      color: ld("#6D5BC9", "#a69dfa"),
      title: t("schadensmeldung"),
      desc: t("erfasstGemeldeteSchaedenFragt"),
      mode: "agent",
    },
    {
      letter: "M",
      color: ld("#2E8C77", "#65bea7"),
      title: t("mietbescheinigung"),
      desc: t("erstelltMietWohngeldbescheinigungenAutomatisch"),
      mode: "vorschlag",
    },
    {
      letter: "S",
      color: ld("#B5832F", "#d4a150"),
      title: t("schluesselbestellung"),
      desc: t("nimmtSchluesselbestellungenBeauftragtPassenden"),
      mode: "agent",
    },
    {
      letter: "Z",
      color: ld("#2F6BAE", "#71aef6"),
      title: t("zaehlerstandErfassen"),
      desc: t("liestUebermittelteZaehlerstaendeTraegt"),
      mode: "agent",
    },
    {
      letter: "M",
      color: ld("#B05B79", "#e68ba9"),
      title: t("mietinteressent"),
      desc: t("beantwortetAnfragenFreienWohnungen"),
      mode: "vorschlag",
    },
    {
      letter: "R",
      color: ld("#3A7D5A", "#79bc96"),
      title: t("eingangsrechnung"),
      desc: t("prueftRechnungenOrdnetLiegenschaft"),
      mode: "vorschlag",
    },
  ];
}

/** Sidebar nav row (Agents group). `indent`/`active` mirror the prototype. */
function NavRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
  readonly indent?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: props.indent === true ? "7px 9px 7px 22px" : "7px 9px",
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: active ? 600 : 400,
        color: active ? H.accent : H.inkSoft,
        background: active ? H.accentSoft : "transparent",
      }}
    >
      <span
        style={{
          color: active ? H.accent : ld("#6B7280", "#a6aaaf"),
          display: "inline-flex",
        }}
      >
        {props.icon}
      </span>
      <span style={{ flex: 1 }}>{props.label}</span>
    </div>
  );
}

/** Single mode chip inside the inert Aus/Vorschlag/Agent toggle. */
function ModeChip(props: {
  readonly label: string;
  readonly bg: string;
  readonly color: string;
}): ReactNode {
  return (
    <span
      style={{
        padding: "4px 9px",
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 6,
        background: props.bg,
        color: props.color,
      }}
    >
      {props.label}
    </span>
  );
}

/** Resolves the per-mode chip background/foreground colors (prototype map). */
function modeStyles(mode: AgentMode): {
  readonly ausBg: string;
  readonly ausColor: string;
  readonly vorBg: string;
  readonly vorColor: string;
  readonly agtBg: string;
  readonly agtColor: string;
} {
  const off = ld("rgba(11,15,25,0.45)", "rgba(250,250,250,0.45)");
  return {
    ausBg: mode === "aus" ? ld("#ECEEF1", "#2e3135") : "transparent",
    ausColor: mode === "aus" ? H.ink : off,
    vorBg: mode === "vorschlag" ? ld("#FBEFD8", "#382d19") : "transparent",
    vorColor: mode === "vorschlag" ? ld("#9A6B16", "#d3a155") : off,
    agtBg: mode === "agent" ? H.accentSoft : "transparent",
    agtColor: mode === "agent" ? H.accent : off,
  };
}

/** One agent card: triangle badge + mode toggle + title + description. */
function AgentTile(props: { readonly agent: AgentCard }): ReactNode {
  const t = useTranslations("marketing.moduleAgents");
  const a = props.agent;
  const m = modeStyles(a.mode);
  return (
    <div
      style={{
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: 14,
        padding: 17,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: 13,
        }}
      >
        <span
          style={{
            position: "relative",
            width: 42,
            height: 38,
            flexShrink: 0,
            display: "inline-flex",
            alignItems: "flex-end",
            justifyContent: "center",
          }}
        >
          <svg
            width={42}
            height={38}
            style={{ position: "absolute", inset: 0, overflow: "visible" }}
            aria-hidden="true"
          >
            <path
              d="M21 4 L37.5 34 L4.5 34 Z"
              fill={a.color}
              stroke={a.color}
              strokeWidth={6}
              strokeLinejoin="round"
            />
          </svg>
          <span
            style={{
              position: "relative",
              color: ld("#fff", "#181b1f"),
              fontSize: 13,
              fontWeight: 700,
              paddingBottom: 4,
            }}
          >
            {a.letter}
          </span>
        </span>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            background: ld("#F4F5F7", "#272a2e"),
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: 8,
            padding: 2,
          }}
        >
          <ModeChip label={t("aus")} bg={m.ausBg} color={m.ausColor} />
          <ModeChip label={t("vorschlag")} bg={m.vorBg} color={m.vorColor} />
          <ModeChip label={t("agent")} bg={m.agtBg} color={m.agtColor} />
        </div>
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          color: H.ink,
          lineHeight: 1.25,
        }}
      >
        {a.title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: H.inkMute,
          lineHeight: 1.5,
          marginTop: 9,
        }}
      >
        {a.desc}
      </div>
    </div>
  );
}

const SIDEBAR_STYLE: CSSProperties = {
  width: SIDEBAR_WIDTH,
  flexShrink: 0,
  borderRight: `1px solid ${ld("#EEF0F3", "#2c2f33")}`,
  background: ld("#FBFBFC", "#1d1f23"),
  padding: "16px 12px",
  boxSizing: "border-box",
};

export function ModuleAgents(_props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleAgents");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      {/* Sidebar */}
      <div style={SIDEBAR_STYLE}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 6px 12px",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700 }}>{t("agents")}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <NavRow
            icon={<Triangle width={16} height={16} strokeWidth={1.8} />}
            label={t("agents")}
          />
          <NavRow
            icon={<Inbox width={16} height={16} strokeWidth={1.9} />}
            label={t("inboxAgenten")}
            active
            indent
          />
          <NavRow
            icon={<MessageCircle width={16} height={16} strokeWidth={1.8} />}
            label={t("kundenChat")}
            indent
          />
          <NavRow
            icon={<LayoutGrid width={16} height={16} strokeWidth={1.8} />}
            label={t("koPilotAgent")}
            indent
          />
        </div>
        <div style={{ height: 1, background: ld("#EEF0F3", "#2c2f33"), margin: "10px 6px" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <NavRow
            icon={<LayoutTemplate width={16} height={16} strokeWidth={1.8} />}
            label={t("vorlagen")}
          />
          <NavRow
            icon={<GraduationCap width={16} height={16} strokeWidth={1.8} />}
            label={t("training")}
          />
          <NavRow
            icon={<BarChart3 width={16} height={16} strokeWidth={1.8} />}
            label={t("statistiken")}
          />
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "20px 22px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 18,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 9,
                  fontSize: 18,
                  fontWeight: 700,
                }}
              >
                <span style={{ display: "inline-flex", width: 20, height: 18 }}>
                  <svg
                    width={20}
                    height={18}
                    style={{ display: "block", overflow: "visible" }}
                    aria-hidden="true"
                  >
                    <path
                      d="M10 3 L17.6 16 L2.4 16 Z"
                      style={{ fill: H.accent, stroke: H.accent }}
                      strokeWidth={3.4}
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                {t("agents")}
              </span>
              <span style={{ fontSize: 13, color: H.inkDim }}>
                {t("k39Agentenmodus")}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
                  borderRadius: 8,
                  padding: "6px 11px",
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: H.ink,
                  cursor: "pointer",
                }}
              >
                <Play width={13} height={13} strokeWidth={1.8} />
                {t("modus")}
                <ChevronDown width={13} height={13} strokeWidth={1.8} />
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  height: 34,
                  padding: "0 11px",
                  background: ld("#F4F5F7", "#272a2e"),
                  border: `1px solid ${ld("#EBEDF0", "#2f3236")}`,
                  borderRadius: 9,
                  color: H.inkDim,
                  fontSize: 13,
                  minWidth: 150,
                  boxSizing: "border-box",
                }}
              >
                <Search width={15} height={15} strokeWidth={1.8} />
                {t("agentenSuchen")}
              </div>
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: H.accent,
                  color: ld("#fff", "#181b1f"),
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Plus width={18} height={18} strokeWidth={2} />
              </span>
            </div>
          </div>

          {/* Card grid */}
          <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 14,
              }}
            >
              {agents(t).map((a) => (
                <AgentTile key={`${a.letter}-${a.title}`} agent={a} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
